<template>
    <main class="min-h-screen p-4 md:p-2">
        <article class="mx-auto max-w-7xl">
            <header class="w-full bg-hero text-white p-8 rounded-lg">
                <UBreadcrumb :links="breadcrumbs" divider="/"
                    :ui="{ active: 'text-primary-200 active', li: 'text-slate-200 gap-2', ol: 'flex-wrap', label: 'label', base: 'font-normal', inactive: 'hover:text-slate-100 hover:underline' }" />
                <div class="mt-8 flex justify-between gap-8 flex-col md:flex-row">
                    <div class="flex flex-col">
                        <div class="text-sm flex gap-2 flex-wrap">
                            <span class="rounded-full border-2 border-slate-500 p-1 px-4"
                                v-for="tag in page.article.tags">{{ tag }}</span>
                        </div>
                        <h1 class="mt-8 text-2xl md:text-4xl leading-[3rem] text-pretty"> {{ page.title }}
                        </h1>
                        <NewsletterInput class="mt-8 md:mt-16 max-w-max" submitText="Subscribe to the
                                Blog" />
                    </div>

                    <div class="max-w-[30rem] hidden lg:block">
                        <PathwayImg :src="page.thumbnail ?? page.article.thumbnail" class="w-[34rem]" />
                        <!-- <NuxtImg :src="page.thumbnail ?? page.article.thumbnail" placeholder /> -->

                    </div>
                </div>
                <div class="flex mt-4 items-center justify-between flex-wrap gap-4">
                    <div class="flex gap-4 items-center flex-wrap">
                        <div class="flex items-center gap-2">
                            <PathwayImg :src="page.author.img.replace('assets/content/blog', 'assets/blog')" alt=""
                                class="w-8 h-8 object-cover rounded-full" />

                            <span class="font-semibold">{{ page.author.name }}</span>
                        </div>
                        <span class="text-gray-400 font-semibold">·</span>
                        <span class="text-gray-400">Published {{ articleDate }}</span>
                        <span class="text-gray-400 font-semibold">·</span>
                        <span class="text-gray-400">Updated {{ updatedDate || articleDate }}</span>
                        <span class="text-gray-400 font-semibold">·</span>
                        <span class="text-gray-400">{{ calcReadingTime() }} min read</span>
                    </div>
                    <div>
                        <ArticleShare :href="currentUrl" :pure="true"
                            linkClass="text-white border-white hover:border-primary-200 hover:text-primary-300 focus:border-primary-200 focus:text-primary-300" />
                    </div>
                </div>
            </header>
            <div>
                <div class="flex gap-8 relative">
                    <aside
                        class="mt-8 sticky top-header left-0 h-full flex flex-col gap-8 w-[20rem] flex-none hidden md:flex">
                        <div class="bg-[#F1F1F2]  p-4 rounded-xl w-full max-h-[20rem] overflow-y-scroll"
                            v-if="toc.links.length">
                            <span class="font-serif font-bold pw-text-black">Contents</span>
                            <DocsTocBlog :links="toc.links" :onlyLinks="true" class="mt-2" />
                        </div>
                        <div class="flex flex-col p-4 bg-hero rounded-xl text-white">
                            <span class="font-bold">Subscribe to the Blog</span>
                            <span class="text-sm mt-2">A newsletter to learn more about what's latest in the world of
                                GenAI
                                and Realtime AI via Pathway.</span>
                            <NewsletterInput class="mt-4 text-sm" />
                            <!-- <div class="flex mt-4 text-sm"><input type="email" placeholder="Your email address"
                                    class="pl-2 rounded-l-lg text-slate-950 w-full" />
                                <button type="button" class="p-2 bg-primary-500 rounded-r-lg">
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </button>
                            </div> -->
                        </div>
                        <div class="flex flex-col items-start border-t pt-4">
                            <span class="text-sm font-bold mb-2">Share this article</span>
                            <ArticleShare :href="currentUrl" :pure="true" />
                        </div>
                    </aside>
                    <section class="mt-4 grow min-w-0">
                        <DocsTocMobile />
                        <div ref="articleContent" class="mb-8">
                            <slot />
                        </div>
                        <hr />
                        <div class="flex justify-center gap-8 flex-wrap items-center">
                            <Author :author="page?.author" v-if="page?.author" />
                            <Author :author="page?.author?.coauthors" v-if="page?.author && page?.author.coauthors" />
                        </div>
                    </section>
                </div>
                <FancyBanner class="mt-8" logoIcon="heroicons:bolt-20-solid">
                    <template #default>
                        <a href="/developers/user-guide/introduction/installation"
                            class="p-4 bg-primary-500 text-white mt-2 flex gap-2 items-center max-w-max rounded-lg font-bold">
                            Get started for free
                            <Icon name="heroicons:arrow-right-16-solid" />
                        </a>
                    </template>
                    <template #logoText>
                        Power your RAG and ETL pipelines with Live Data
                    </template>
                </FancyBanner>
                <div v-if="related.length" class="overflow-x-auto w-full mx-auto p-4 mt-8">
                    <span class="text-2xl font-serif font-bold">Related Articles</span>
                    <ul class="grid grid-cols-[repeat(auto-fill,minmax(19rem,1fr))] gap-8 mt-8 flex-wrap">
                        <li v-for="article in related" class="h-full">
                            <ArticleTile :article="article" class="h-full max-w-[25rem]" />
                        </li>
                    </ul>
                </div>

                <DocsPrevNext class="mb-8" />
                <!-- <ArticleFooter :page="page" :article="page.article" :related="related" :currentUrl="currentUrl"
                    :hasComments="hasComments" class="mt-8" /> -->
            </div>
        </article>
    </main>
</template>

<script setup>
const { page, toc } = useContent()
const related = await getRelatedArticles(page.value) ?? []
const currentUrl = computed(() => process.client ? window.location.href : 'https://pathway.com' + page.value?._path)

const articleDate = new Date(page.value?.date ?? page.value?.article?.date)?.toLocaleString('en-EN', {
    month: 'long', day: 'numeric',
    year: 'numeric'
}) ?? ''

const updatedDate = page.value?.article?.updatedDate ? new Date(page.value?.updatedDate ?? page.value?.article?.updatedDate)?.toLocaleString('en-EN', {
    month: 'long', day: 'numeric',
    year: 'numeric'
}) : false


const articleContent = ref(null)
function calcReadingTime () {
    const wordsPerMinute = 240
    const noOfWords = articleContent.value?.textContent?.split(/\s/g)?.length || 0
    const minutes = noOfWords / wordsPerMinute
    const readTime = Math.ceil(minutes)
    return readTime
}

const breadcrumbs = [{
    label: 'Home',
    to: '/'
}, {
    label: 'Blog',
    to: '/blog'
}, {
    label: page.value.title,
}]
</script>

<style scoped lang="postcss">
.bg-hero {
    background: #141d2d;
    background: linear-gradient(90deg, #141d2d 42%, #152033 50%, #2729b0)
}

::v-deep() {
    .label {
        overflow: visible;
    }

    .active .label {
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
